/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {PICKLIST_INITIAL_STATE} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux";
import {handle_picklist_action} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux";
import {fetchAndHandleResponse} from "../../../../utils";
import {NEOFAX_PAGE_NAME} from "../../../../common/constants";
import {
    ACTIVE_STATUS,
    ERROR_STATUS,
    NONE_STATUS, SUCCEED_STATUS
} from "../../../../../../components/response-loading/redux/response-loading-redux";

const ACTION_TYPES = {
    IV_PICKLIST_SET_DATA_ARRAY_ACTION: 'IV_PICKLIST_SET_DATA_ARRAY_ACTION',
    IV_PICKLIST_SET_SEARCH_TEXT_ACTION: 'IV_PICKLIST_SET_SEARCH_TEXT_ACTION',
}

const processResponse = (responseBody, dispatch, params) => {
    const {picklist_id, page_name} = params;
    if (responseBody && responseBody.recordSet?.records) {
        dispatch(saveDrugs({
            drugs: responseBody.recordSet?.records,
            picklist_id,
            page_name
        }));
    }
};

export const fetchAndSaveDrugs = createAsyncThunk('neofax_drugs/fetchDrugs', async (params, {
    dispatch,
    rejectWithValue
}) => {
    return await fetchAndHandleResponse(params, dispatch, rejectWithValue, processResponse);
});

const prepareDataArray = (payload) => {
    return payload.map(item => [
        item.title,                 // long_names
        item.docId,                 // document_id
        "",                         // original_name (empty)
        undefined,                  // _
        "",                         // generic_list_upper (empty)
        { isSplitDisabled: true }   // is_split_disabled (true/false)
    ]);
};


const initialState = {
    drug_data: [],
    error: null,
    neofax_picklist: {...PICKLIST_INITIAL_STATE, status: NONE_STATUS},
    pediatrics_picklist: {...PICKLIST_INITIAL_STATE, status: NONE_STATUS},
    selectedTabIndex: 0,
    isStateCleared: false
};
const neofaxDrugsSlice = createSlice({
    name: 'neofax_search_page',
    initialState: initialState,
    reducers: {
        saveDrugs: (state, action) => {
            const {drugs, picklist_id, page_name} = action.payload;
            action.type = ACTION_TYPES.IV_PICKLIST_SET_DATA_ARRAY_ACTION;
            action.page_name = page_name;
            action.data_array = prepareDataArray(drugs);
            action.picklist_id = picklist_id;
            state[picklist_id] = handle_picklist_action(NEOFAX_PAGE_NAME, state, action)[picklist_id];
            state[picklist_id].picklist_display_array = state[picklist_id].data_array;
            state[picklist_id].no_results_found_flag = false;
            state.isStateCleared = false;
        },
        setSearchText: (state, action) => {
            const {value, picklist_id, page_name} = action.payload;
            action.type = ACTION_TYPES.IV_PICKLIST_SET_SEARCH_TEXT_ACTION;
            action.page_name = page_name;
            action.picklist_id = picklist_id;
            action.search_text = value;
            const stateUpdate = handle_picklist_action(NEOFAX_PAGE_NAME, state, action);
            if (stateUpdate !== null) {
                state[picklist_id] = handle_picklist_action(NEOFAX_PAGE_NAME, state, action)[picklist_id];
            }
            if (value === "") {
                state[picklist_id].picklist_display_array = state[picklist_id].data_array;
                state[picklist_id].no_results_found_flag = false;
            }
        },
        setIsPulldownOpen: (state, action) => {
            const {picklist_id, isPulldownOpen} = action.payload;
            state[picklist_id].isPulldownOpen = isPulldownOpen;
        },
        setSelectedTabIndex: (state, action) => {
            state.selectedTabIndex = action.payload;
        },
        clearNeofaxPediatricsSearchState: (state) => {
            return {...initialState, isStateCleared: true};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAndSaveDrugs.pending, (state, action) => {
                const picklist_id = action.meta.arg?.picklist_id;
                state[picklist_id].status = ACTIVE_STATUS;
            })
            .addCase(fetchAndSaveDrugs.fulfilled, (state, action) => {
                state.drug_data = action.payload;
                state.error = null;
                const picklist_id = action.meta.arg?.picklist_id;
                state[picklist_id].status = SUCCEED_STATUS;
            })
            .addCase(fetchAndSaveDrugs.rejected, (state, action) => {
                state.error = action.error.message;
                const picklist_id = action.meta.arg?.picklist_id;
                state[picklist_id].status = ERROR_STATUS;
            });
    }
});

export const {saveDrugs, setSearchText, setIsPulldownOpen, setSelectedTabIndex, clearNeofaxPediatricsSearchState} = neofaxDrugsSlice.actions;

export const selectDrugsStatus = (state) => (picklist_id) => state.neofax_search_page[picklist_id].status;
export const selectNeonatalDrugs = (state) => state.neofax_search_page.neofax_picklist;
export const selectPediatricDrugs = (state) => state.neofax_search_page.pediatrics_picklist;
export const selectNeonatalLoadingState = (state) => state.neofax_search_page.neofax_picklist.status;
export const selectPediatricLoadingState = (state) => state.neofax_search_page.pediatrics_picklist.status;
export const selectedTabIndex = (state) => state.neofax_search_page.selectedTabIndex;
export const selectIsStateCleared = (state) => state.neofax_search_page.isStateCleared;


export default neofaxDrugsSlice.reducer;
